import { parsePhoneNumber } from 'libphonenumber-js';

import { maskPhoneNumber } from './phone';
import TrackerService from './TrackerService';

/**
 * Best-effort Mixpanel instrumentation for Firebase phone-auth SMS sends.
 *
 * Firebase dispatches the SMS browser→Google directly, so there's no server
 * chokepoint to count at — instead each client reports its own send here, right
 * after signInWithPhoneNumber resolves (resolve = SMS actually sent = cost incurred).
 *
 * This is a COST/FRAUD THERMOMETER, not a gate: it tracks what our own apps
 * trigger (≈ the real bill for legitimate traffic) and surfaces country mismatches;
 * it won't see a scripted bypass. Reconcile monthly with Firebase Auth → Usage.
 *
 * Event name is snake_case (house style, e.g. client_sign_in); custom properties
 * are camelCase (Mixpanel's own $-fields stay snake_case).
 */

// Rough estimated Firebase SMS unit cost by the NUMBER's country, in its native
// currency. Verify/adjust against the GCP invoice — these are just for the trend line.
const GBP_TO_EUR = 1.17;
const USD_TO_EUR = 0.92;

// EU/EEA countries we serve (mirrors utils/phone COUNTRIES, minus GB and US).
const EUR_COUNTRIES = new Set([
  'IE', 'AT', 'BE', 'CY', 'EE', 'FI', 'FR', 'DE', 'GR', 'HR', 'IT', 'LV', 'LT',
  'LU', 'MT', 'NL', 'PT', 'SK', 'SI', 'ES', 'BG', 'CZ', 'DK', 'HU', 'PL', 'RO',
  'SE', 'IS', 'NO',
]);

const round4 = (n) => Math.round(n * 10000) / 10000;

/** Estimated SMS cost for a number's country → { amount, currency, amountEur }. */
export function estimateSmsCost(phoneCountry) {
  if (phoneCountry === 'GB') return { amount: 0.04, currency: 'GBP', amountEur: round4(0.04 * GBP_TO_EUR) };
  if (phoneCountry === 'US') return { amount: 0.01, currency: 'USD', amountEur: round4(0.01 * USD_TO_EUR) };
  if (EUR_COUNTRIES.has(phoneCountry)) return { amount: 0.06, currency: 'EUR', amountEur: 0.06 };
  // Any other (shouldn't happen — SMS login is limited to EUR/UK/US countries).
  return { amount: 0.01, currency: 'USD', amountEur: round4(0.01 * USD_TO_EUR) };
}

function phoneCountryFromE164(e164) {
  try {
    const parsed = parsePhoneNumber(e164);
    return parsed ? (parsed.country || null) : null;
  } catch {
    return null;
  }
}

// Shared identity/country props for both the requested and completed events.
function baseProps({ e164, inputCountry, profileCountry, context, app = 'strikepay' }) {
  const phoneCountry = phoneCountryFromE164(e164);
  const expectedCountry = profileCountry || inputCountry || null;
  return {
    phoneCountry,
    maskedNumber: maskPhoneNumber(e164),
    inputCountry: inputCountry || null,
    profileCountry: profileCountry || null,
    countryMatch: !!phoneCountry && !!expectedCountry && phoneCountry === expectedCountry,
    smsContext: context,
    provider: 'firebase',
    platform: 'web',
    app,
  };
}

/**
 * Fire sms_verification_requested (an SMS was sent = cost incurred). Best-effort —
 * never throws into the auth flow.
 *
 * @param {object} p
 * @param {string} p.e164          the number the SMS was sent to (E.164)
 * @param {string} [p.inputCountry] country picked in the UI
 * @param {string} [p.profileCountry] client.country, when known
 * @param {'signup'|'login'|'add_login'} p.context
 * @param {'strikepay'|'strike-payer'} [p.app]
 */
export function trackSmsVerificationRequested(p) {
  try {
    const base = baseProps(p);
    const cost = estimateSmsCost(base.phoneCountry);
    new TrackerService({}).mixpanelTrack('sms_verification_requested', {
      ...base,
      estimatedSmsCost: cost.amount,
      estimatedSmsCostCurrency: cost.currency,
      estimatedSmsCostEur: cost.amountEur,
    });
  } catch (err) {
    console.warn('[smsTracking] failed to record SMS send:', err && err.message);
  }
}

/**
 * Fire sms_verification_completed (the code was accepted). Pairs with the requested
 * event so a Mixpanel funnel shows completion/conversion — a low ratio flags a broken
 * flow or bots that pump codes but never finish. No cost (that's on the send).
 * Best-effort — never throws.
 */
export function trackSmsVerificationCompleted(p) {
  try {
    // sendBeacon: the login flow redirects to the dashboard the instant this fires, so a
    // normal batched send can be dropped by the navigation. Beacon survives the page change.
    new TrackerService({}).mixpanelTrack(
      'sms_verification_completed', baseProps(p), { transport: 'sendBeacon' },
    );
  } catch (err) {
    console.warn('[smsTracking] failed to record SMS completion:', err && err.message);
  }
}
